import React, { useState } from "react";
import {
  Container,
  Button,
  Typography,
  Box,
  Alert,
  Paper,
  CircularProgress,
} from "@mui/material";
import { Email as EmailIcon } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { unsubscribePromotions } from "../apis/userApi";
import axios from "axios";

const UnsubscribePromotions: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const handleUnsubscribe = async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError("");
      setMessage("");
      const data = await unsubscribePromotions(token);
      setMessage(
        data.message ||
          "You have been unsubscribed from promotional emails. Booking, membership, and account emails will still be sent."
      );
    } catch (err) {
      const apiMessage = axios.isAxiosError(err)
        ? err.response?.data?.message
        : undefined;
      setError(
        typeof apiMessage === "string" && apiMessage
          ? apiMessage
          : "We couldn't process this unsubscribe link. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 2,
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        }}
      >
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box
            sx={{
              backgroundColor: "primary.light",
              width: 70,
              height: 70,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <EmailIcon sx={{ fontSize: 36, color: "white" }} />
          </Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Promotional emails
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Unsubscribe to stop promotional emails. Booking, membership, and
            account emails will still be sent.
          </Typography>
        </Box>

        {!token && (
          <Alert severity="error" sx={{ borderRadius: 1 }}>
            This unsubscribe link is invalid.
          </Alert>
        )}

        {token && !message && (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={loading}
            onClick={handleUnsubscribe}
            sx={{
              py: 1.5,
              fontWeight: "bold",
              textTransform: "none",
              fontSize: "1rem",
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Unsubscribe from promotional emails"
            )}
          </Button>
        )}

        {message && (
          <Alert severity="success" sx={{ borderRadius: 1 }}>
            {message}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: message ? 2 : 3, borderRadius: 1 }}>
            {error}
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default UnsubscribePromotions;
